import React, { useState, useCallback } from 'react'
import { Typography, Progress, Tooltip } from 'antd'
import { useIntl } from 'react-intl'
import { PiInfoBold, PiClipboardTextBold } from 'react-icons/pi'
import IntlMessages from '@vacationtracker/shared/components/utils/IntlMessages'
import { getColorFromProgress } from '@vacationtracker/shared/functions/user-insights/energy-visualization'
import { EnergyFactor } from '../../types'

const { Title, Text } = Typography

interface ScoreBreakdownProps {
  scoreBreakdown: EnergyFactor[]
  onTooltipView?: (tooltipType: 'energy_score' | 'trend_point' | 'factor_breakdown' | 'pto_suggestion' | 'department_comparison' | 'energy_mode',
    tooltipContent: string, elementHovered: string, section: 'energy_gauge' | 'trend_chart' | 'suggestions' | 'score_breakdown', hoverDurationMs: number) => void
  onActionTaken?: (actionType: 'button_click' | 'chart_interaction' | 'link_click' | 'gauge_click',
    actionName: string, elementType: 'button' | 'link' | 'chart' | 'gauge' | 'card' | 'suggestion',
    section: 'header' | 'energy_gauge' | 'trend_chart' | 'suggestions' | 'score_breakdown', ledToNavigation?: boolean, destinationUrl?: string) => void
}

const ScoreBreakdown: React.FC<ScoreBreakdownProps> = ({ scoreBreakdown, onTooltipView, onActionTaken }) => {
  const { formatMessage } = useIntl()
  const [tooltipStartTimes, setTooltipStartTimes] = useState<Record<string, number>>({})

  // Table layout styles are now in LESS file

  const getTooltipText = (name: string) => {
    const componentName = name.toLowerCase()

    switch (componentName) {
      case 'pto balance':
        return formatMessage({ id: 'energyLevel.ptoBalanceTooltip' })
      case 'recent rest':
        return formatMessage({ id: 'energyLevel.recentRestTooltip' })
      case 'leave pattern':
        return formatMessage({ id: 'energyLevel.leavePatternTooltip' })
      case 'accrual health':
        return formatMessage({ id: 'energyLevel.accrualHealthTooltip' })
      case 'rollover health':
        return formatMessage({ id: 'energyLevel.rolloverHealthTooltip' })
      case 'work-life balance':
        return formatMessage({ id: 'energyLevel.workLifeBalanceTooltip' })
      case 'planning':
        return formatMessage({ id: 'energyLevel.planningTooltip' })
      default:
        return formatMessage({ id: 'energyLevel.defaultTooltip' })
    }
  }

  const handleTooltipOpen = useCallback((factorName: string) => {
    const key = `factor_${factorName}`
    setTooltipStartTimes(prev => ({ ...prev, [key]: Date.now() }))
  }, [])

  const handleTooltipClose = useCallback((factorName: string) => {
    const key = `factor_${factorName}`
    const startTime = tooltipStartTimes[key]

    if (startTime && onTooltipView) {
      const hoverDuration = Date.now() - startTime
      const tooltipText = getTooltipText(factorName)

      onTooltipView(
        'factor_breakdown',
        tooltipText,
        `factor_${factorName}`,
        'score_breakdown',
        hoverDuration
      )
    }

    // Clean up the start time
    setTooltipStartTimes(prev => {
      const newTimes = { ...prev }
      delete newTimes[key]
      return newTimes
    })
  }, [tooltipStartTimes, onTooltipView, scoreBreakdown])

  return (
    <div className="score-breakdown">
      <Title level={4}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
          <PiClipboardTextBold style={{ verticalAlign: 'middle' }} />
          <IntlMessages id="energyLevel.scoreBreakdownTitle" />
        </span>
      </Title>

      <div className="header-separator" />

      <Text type="secondary" className="score-breakdown-description">
        <IntlMessages id="energyLevel.scoreBreakdownDescription" />
      </Text>

      <div className="score-breakdown-table">
        {scoreBreakdown.map((factor, index) => {
          // Calculate percentage for progress bar (0-100)
          const progressPercent = factor.maxWeight ? Math.round((factor.weight / factor.maxWeight) * 100) : factor.score

          // Get color based on progress, matching avatar color logic
          const strokeColor = getColorFromProgress(progressPercent)

          return (
            <div key={index} className="score-breakdown-row">
              {/* Label with info icon */}
              <div className="score-breakdown-cell">
                <Text strong style={{ marginRight: 8 }}>
                  {factor.name}
                </Text>
                <Tooltip
                  title={getTooltipText(factor.name)}
                  placement="top"
                  onOpenChange={(open) => {
                    if (open) {
                      handleTooltipOpen(factor.name)
                    } else {
                      handleTooltipClose(factor.name)
                    }
                  }}
                >
                  <Text style={{ color: '#8c8c8c', cursor: 'pointer', fontSize: '14px' }}>
                    <PiInfoBold />
                  </Text>
                </Tooltip>
              </div>

              {/* Progress bar - 12 steps to match avatar segments */}
              <div className="score-breakdown-cell progress-cell">
                <Progress
                  percent={progressPercent}
                  strokeColor={strokeColor}
                  showInfo={false}
                  steps={12}
                  size="small"
                />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default ScoreBreakdown
